import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

// The worker file is copied to /assets by an angular.json asset rule (from
// node_modules/pdfjs-dist/build), so the version always matches the installed
// pdfjs (v2 in this portal).
GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.js';

/** True if the bytes start with the `%PDF` signature. */
export function isPdf(bytes: Uint8Array): boolean {
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
}

/**
 * Renders the first page of a PDF to a canvas. A higher scale yields more
 * pixels per QR module, which matters for dense codes (e.g. CNH-e).
 */
export async function renderPdfFirstPage(
  bytes: Uint8Array,
  scale = 3,
): Promise<HTMLCanvasElement> {
  // getDocument may transfer the buffer to the worker; hand it a private copy.
  const loadingTask = getDocument({ data: bytes.slice() });
  try {
    const doc = await loadingTask.promise;
    const page = await doc.getPage(1);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get a 2D canvas context');
    }
    // pdfjs v2 render signature (no `canvas` field).
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas;
  } finally {
    loadingTask.destroy();
  }
}

/** Loads an image File into an HTMLImageElement (resolved once decoded). */
export function loadImageElement(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load the selected image'));
    };
    img.src = url;
  });
}

/**
 * Resolves a File (image or PDF) to a canvas containing the image / first page.
 * Centralises the "render whatever was uploaded to pixels" step the scanners share.
 */
export async function fileToCanvas(file: Blob): Promise<HTMLCanvasElement> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (isPdf(bytes)) {
    return renderPdfFirstPage(bytes);
  }
  const img = await loadImageElement(file);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get a 2D canvas context');
  }
  ctx.drawImage(img, 0, 0);
  return canvas;
}
